import { usePostOrderApi, usePutOrderApi } from 'api/order'
import { Flex } from 'components/shared/flex'
import { DateField, InputField, SelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useState } from 'react'
import { Button, Modal, Table } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { Order, OrderForm } from 'entities/order'
import { MasterDataContext } from 'contexts/master_data_context'
import SelectProbeModal from 'components/probe/select_probe_modal'
import { Probe } from 'entities/probe'

const FormView = (form: Form<OrderForm>, apiError: ApiError) => {
    const masterData = useContext(MasterDataContext)
    return (
        <>
            <Flex>
                <DateField form={form} attr="date" label="Date" required />
            </Flex>
            <Flex>
                <SelectField
                    form={form}
                    attr="type"
                    label="Type"
                    required
                    selectItems={[96, 16].map((n) => {
                        return { label: n, value: n }
                    })}
                    includeBlank
                />
            </Flex>
            <Flex>
                <InputField form={form} attr="salesOrder" label="Sales order" />
            </Flex>

            <Table>
                <thead>
                    <tr>
                        <th style={{ minWidth: 200 }}>Probe</th>
                        <th style={{ width: 200 }}>AssayID</th>
                        <th style={{ width: 100 }}>Quantity</th>
                        {/* <th>SortNumber</th> */}
                    </tr>
                </thead>
                <tbody>
                    {form.object.orderItems?.map((orderItem, index) => {
                        return (
                            <tr key={'select-order-item-' + index}>
                                <td>{orderItem.probe?.name}</td>
                                <td>{orderItem.probe?.assayId}</td>
                                <td>
                                    <InputField form={form} attr={['orderItems', index, 'quantity']} type="number" />
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </Table>

            <SelectProbeModal
                openButtonStyle={{ width: '100%', marginTop: 10 }}
                openButtonText="購入製品追加"
                executeButtonText="追加"
                onClick={(probes: Probe[]) => {
                    form.update((f) => {
                        f.orderItems = probes.map((p) => {
                            return { probeId: p.id, probe: p, quantity: 1, orderId: form.object.id }
                        })
                    })
                }}
                initialItems={form.object.orderItems?.map((oi) => oi.probe!) ?? []}
            />
            <InputField form={form} attr="note" label="備考" as="textarea" />
        </>
    )
}

type NewOrderFormProps = {
    onComplete?: () => void
}

export const NewOrderForm = (props: NewOrderFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostOrderApi()
    const form = useForm<OrderForm>({})
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Orderの登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>{FormView(form, api.apiError)}</>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

type EditOrderFormProps = {
    order: Order
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditOrderForm = (props: EditOrderFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutOrderApi()
    const form = useForm<OrderForm>({ ...props.order })

    useEffectSkipFirst(() => {
        form.set(props.order)
    }, [props.order])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Orderの編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {form.object.id && (
                    <>
                        <h3 style={{ marginBottom: 10 }}>ID: {props.order.id}</h3>
                        {FormView(form, api.apiError)}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
